import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Plus,
  Factory,
  MapPin,
  Mail,
  Phone,
  Clock,
  Cpu,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Pencil,
  Trash2,
  Loader2,
  Save,
  Building2,
  Globe,
} from "lucide-react";
import { plantsAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";

/**
 * Plant Form Modal for Add & Edit
 * Matches the structure, layout, and styling of AddMachine.jsx
 */
const PlantModal = ({ isOpen, plantToEdit, onClose }) => {
  const queryClient = useQueryClient();
  const isEditing = Boolean(plantToEdit);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    address: "",
    timezone: "UTC",
    contactEmail: "",
    contactPhone: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (plantToEdit) {
        setFormData({
          name: plantToEdit.name || "",
          location: plantToEdit.location || "",
          address: plantToEdit.address || "",
          timezone: plantToEdit.timezone || "UTC",
          contactEmail: plantToEdit.contact_email || plantToEdit.contactEmail || "",
          contactPhone: plantToEdit.contact_phone || plantToEdit.contactPhone || "",
        });
      } else {
        setFormData({
          name: "",
          location: "",
          address: "",
          timezone: "UTC",
          contactEmail: "",
          contactPhone: "",
        });
      }
    }
  }, [isOpen, plantToEdit]);

  const saveMutation = useMutation({
    mutationFn: (payload) => {
      if (isEditing) {
        return plantsAPI.update(plantToEdit.id, payload);
      }
      return plantsAPI.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plants"] });
      if (plantToEdit) {
        queryClient.invalidateQueries({ queryKey: ["plant", String(plantToEdit.id)] });
        toast.success("Plant updated successfully!");
      } else {
        toast.success("Plant facility registered successfully!");
      }
      onClose();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
        (isEditing ? "Failed to update plant" : "Failed to register plant facility")
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Plant name is required");
      return;
    }
    saveMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saveMutation.isPending) {
            onClose();
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <Factory className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  {isEditing ? "Edit Plant Facility" : "Add Plant Facility"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isEditing
                    ? "Update facility information, contacts and location"
                    : "Register industrial facility for machines and monitoring"}
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* Row 1: Plant Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Plant Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. North Assembly Plant"
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Row 2: Location & Timezone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Location / Unit
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g. Building B, Sector 4"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Timezone
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      placeholder="e.g. Asia/Kolkata or UTC"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Contact Email & Contact Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Contact Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      placeholder="plant.ops@example.com"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Contact Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      placeholder="+1 (555) 019-2834"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Facility Address */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Full Facility Address
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street, City, State, Country, Postal Code"
                    className="w-full px-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={saveMutation.isPending}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    {isEditing ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  <>
                    {isEditing ? (
                      <Save className="w-4 h-4 mr-1.5" />
                    ) : (
                      <Plus className="w-4 h-4 mr-1.5" />
                    )}
                    {isEditing ? "Save Changes" : "Add Plant"}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

/**
 * Plant Card - Mirrors MachineCard styling and micro-interactions
 */
const PlantCard = ({ plant, index, onEdit, onDelete }) => {
  const machineCount = Number(plant.machine_count) || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
      className="h-full"
    >
      <Link
        to={`/plants/${plant.id}`}
        className="group bg-white rounded-2xl border border-slate-200/80 p-5 hover:border-blue-300 hover:shadow-md transition-all duration-200 flex flex-col justify-between h-full relative block cursor-pointer"
      >
        <div>
          {/* Header: Icon, Name, Location & Actions */}
          <div className="flex items-start justify-between gap-2.5 mb-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                <Factory className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors text-sm sm:text-base leading-snug break-words">
                  {plant.name}
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5 break-words">
                  {plant.location || "Facility Unit"}
                </p>
                <p className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1 break-words">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span>{plant.timezone || "UTC"}</span>
                </p>
              </div>
            </div>

            {/* Action Buttons: Edit & Delete */}
            <div className="flex items-center gap-1 flex-shrink-0 -mr-1 -mt-0.5">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(plant);
                }}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Plant"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(plant);
                }}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                title="Delete Plant"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Plant Info: Contact Email, Phone & Address */}
          <div className="space-y-2 mb-2.5">
            {/* Contact Email (Full width to show entire email address) */}
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span>Contact Email</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 break-all leading-snug">
                {plant.contact_email || "No email specified"}
              </p>
            </div>

            {/* Phone & Location Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                  <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span>Phone</span>
                </p>
                <p className="text-xs font-semibold text-slate-700 break-words leading-snug">
                  {plant.contact_phone || "No phone"}
                </p>
              </div>

              <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span>Location</span>
                </p>
                <p className="text-xs font-semibold text-slate-700 break-words leading-snug">
                  {plant.location || "Main Site"}
                </p>
              </div>
            </div>
          </div>

          {/* Address if available */}
          {plant.address && (
            <p className="text-xs text-slate-500 font-medium line-clamp-1 mb-2 px-0.5 flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span>{plant.address}</span>
            </p>
          )}
        </div>

        {/* Footer: Active Equipment count on left & Status badge on right */}
        <div className="pt-3.5 border-t border-slate-100 flex items-center justify-between gap-2 text-xs mt-auto">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
            <Cpu className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
            <span>
              {machineCount} equipment unit{machineCount === 1 ? "" : "s"}
            </span>
          </span>

          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ml-auto bg-emerald-50 text-emerald-700 border-emerald-200/80">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
            Active Facility
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const Plants = () => {
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [scaleFilter, setScaleFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [deletingPlant, setDeletingPlant] = useState(null);
  const filterRef = useRef(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id) => plantsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plants"] });
      toast.success("Plant deleted successfully");
      setDeletingPlant(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to delete plant");
    },
  });

  const handleDeleteConfirm = () => {
    if (deletingPlant) {
      deleteMutation.mutate(deletingPlant.id);
    }
  };

  const { data: responseData, isLoading } = useQuery({
    queryKey: ["plants"],
    queryFn: () => plantsAPI.getAll().then((res) => res.data),
  });

  const rawPlants = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData)
      ? responseData
      : [];

  // Filter plants by search and scale filter (equipment count)
  const filteredPlants = rawPlants.filter((plant) => {
    const matchesSearch =
      !search ||
      plant.name?.toLowerCase().includes(search.toLowerCase()) ||
      plant.location?.toLowerCase().includes(search.toLowerCase()) ||
      plant.address?.toLowerCase().includes(search.toLowerCase()) ||
      plant.contact_email?.toLowerCase().includes(search.toLowerCase());

    if (!matchesSearch) return false;

    const count = Number(plant.machine_count) || 0;
    if (scaleFilter === "has_machines") return count > 0;
    if (scaleFilter === "empty") return count === 0;
    if (scaleFilter === "large") return count >= 5;

    return true;
  });

  const totalItems = filteredPlants.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const displayedPlants = filteredPlants.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterRef]);

  const scaleOptions = [
    { value: "", label: "All Facilities" },
    { value: "has_machines", label: "Active Equipment (>0)" },
    { value: "large", label: "Large Scale (5+ machines)" },
    { value: "empty", label: "No Equipment Assigned" },
  ];

  const selectedScaleLabel =
    scaleOptions.find((opt) => opt.value === scaleFilter)?.label || "All Facilities";

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Industrial Plants & Facilities
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Operational sites, regional facilities & connected machine distribution
          </p>
        </div>

        <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
          <button
            type="button"
            onClick={() => {
              setEditingPlant(null);
              setIsAddModalOpen(true);
            }}
            className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 shadow-md shadow-blue-500/20 whitespace-nowrap self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            Add Plant
          </button>
        </HasPermission>
      </div>

      {/* Control Bar: Search & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Bar */}
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search by facility name, location, address..."
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-sm transition-all"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdown */}
        <div className="flex items-center gap-2">
          <div ref={filterRef} className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-sm hover:border-slate-400"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                <span className="font-semibold text-slate-700">{selectedScaleLabel}</span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isFilterOpen && "rotate-180"
                )}
              />
            </button>
            <AnimatePresence>
              {isFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-56 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                >
                  {scaleOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setScaleFilter(opt.value);
                        setCurrentPage(1);
                        setIsFilterOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        scaleFilter === opt.value
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>{opt.label}</span>
                      {scaleFilter === opt.value && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Loading Skeletons */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3.5 animate-pulse"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-200" />
                  <div className="space-y-1.5">
                    <div className="h-4 w-28 bg-slate-200 rounded" />
                    <div className="h-3 w-16 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="h-6 w-20 bg-slate-200 rounded-full" />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="h-12 bg-slate-100 rounded-xl" />
                <div className="h-12 bg-slate-100 rounded-xl" />
              </div>
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="h-3.5 w-24 bg-slate-100 rounded" />
                <div className="h-3.5 w-16 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : displayedPlants && displayedPlants.length > 0 ? (
        /* Plant Cards Grid */
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
            {displayedPlants.map((plant, index) => (
              <PlantCard
                key={plant.id}
                plant={plant}
                index={index}
                onEdit={(p) => setEditingPlant(p)}
                onDelete={(p) => setDeletingPlant(p)}
              />
            ))}
          </div>

          {/* Pagination Controls - only shown after 12 items (totalItems > 12) */}
          {!isLoading && totalItems > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, totalItems)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
                facilities
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <Factory className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
            No Facilities Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
            {search || scaleFilter
              ? "No plant facilities matched your current search and filter criteria. Try resetting your search."
              : "No industrial plant facilities are currently registered. Add your first facility to organize equipment."}
          </p>
          {search || scaleFilter ? (
            <button
              onClick={() => {
                setSearch("");
                setScaleFilter("");
                setCurrentPage(1);
              }}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-4"
            >
              Reset Filters
            </button>
          ) : (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
              <button
                type="button"
                onClick={() => {
                  setEditingPlant(null);
                  setIsAddModalOpen(true);
                }}
                className="btn btn-primary inline-flex text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
              >
                <Plus className="w-4 h-4" />
                Add Plant
              </button>
            </HasPermission>
          )}
        </div>
      )}

      {/* Add / Edit Plant Popup Modal */}
      <PlantModal
        isOpen={isAddModalOpen || Boolean(editingPlant)}
        plantToEdit={editingPlant}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingPlant(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {deletingPlant && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => {
                  if (!deleteMutation.isPending) setDeletingPlant(null);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Delete Plant Facility
                      </h2>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-slate-800">
                      {deletingPlant.name}
                    </span>
                    ? This will deactivate the facility record from active views.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeletingPlant(null)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={handleDeleteConfirm}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Facility</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default Plants;